import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Icon,
  Loader,
  Message,
  Card,
  Table,
  Modal,
  Form,
  TextArea,
  Progress,
  Label,
  Segment,
  Grid,
  Header,
  Divider,
  Menu,
  List,
  Popup,
  Step,
} from 'semantic-ui-react';
import { whatsAppApiService, getWhatsAppApiErrorMessage } from '../../services/whatsapp/WhatsAppApiService';
import {
  extractZenviaTemplateSenderId,
  type ZenviaTemplateListItem,
  type ZenviaCampaignListItem,
  type ZenviaCampaignDetail,
  type ZenviaCampaignRecipientsPage,
  type CsvPreviewResponse,
  type ZenviaCampaignRecipientInput,
  type ZenviaCampaignListFilter,
  type ZenviaCampaignRecipientsFilter,
  type ZenviaCampaignMetrics,
  type ZenviaRecreateCampaignRequest,
  type ZenviaRecreateSource,
} from '../../types/zenvia.types';
import type { ClientListResponse } from '../../types/dtos';
import useToast from '../../hooks/useToast';
import {
  extractZenviaTemplateVariableKeys,
  classifyZenviaTemplateVariableKeys,
  templateVariableDisplayGroup,
  templateVariableOriginBadgePt,
  templateVariableOriginHelpPt,
} from './zenviaTemplateWizard.utils';
import type { ClassifiedTemplateVariable } from './zenviaTemplateWizard.utils';
import { downloadExampleCsv, readTemplateExamples } from './campaignCsvExample';
import {
  mapClientToTemplateFields,
  buildClienteWhatsAppMetadataFields,
  normalizeCampaignPhoneDigits,
  classifyTemplateKeys,
  isLikelyButtonPayloadKey,
  normalizeTemplateOrColumnKey,
  csvColumnAliasesForTemplateKey,
} from './clientTemplateFieldMap';
import ClientPickerModal from '../../components/WhatsApp/ClientPickerModal';

type SuiLabelColor = 'red' | 'orange' | 'yellow' | 'olive' | 'green' | 'teal' | 'blue' | 'violet' | 'purple' | 'pink' | 'brown' | 'grey' | 'black';

function statusColor(status: string): SuiLabelColor {
  const s = status.toLowerCase();
  if (s === 'completed') return 'green';
  if (s === 'failed') return 'red';
  if (s === 'cancelled') return 'grey';
  if (s === 'running' || s === 'queued') return 'blue';
  if (s === 'draft') return 'yellow';
  return 'grey';
}

function progressPercent(c: ZenviaCampaignListItem): number {
  const t = c.totalRecipients;
  if (!t) return 0;
  return Math.round(((c.sentCount + c.failedCount) / t) * 100);
}

function recipientStatusColor(status: string): SuiLabelColor {
  const s = status.toLowerCase();
  if (s === 'sent') return 'green';
  if (s === 'failed') return 'red';
  return 'yellow';
}

type CreateRecipientsTab = 'phones' | 'csv';

interface BatchesLocationState {
  phones?: string[];
  prefillClients?: ClientListResponse[];
}

type RecipientSource = 'manual' | 'fromClients';

function recipientFieldsProvidesKey(fields: Record<string, string> | undefined, key: string): boolean {
  if (!fields) return false;
  if (Object.prototype.hasOwnProperty.call(fields, key) && String(fields[key] ?? '').trim() !== '') {
    return true;
  }
  const kn = key.trim().toLowerCase();
  for (const [k, val] of Object.entries(fields)) {
    if (k.trim().toLowerCase() === kn && String(val ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
}

/**
 * Cada chave deve estar no JSON padrão ou em todos os destinatários em `fields`.
 */
function templateKeysMissingCoverageStructured(
  templateKeys: string[],
  defaultFields: Record<string, string> | undefined,
  recipients: ZenviaCampaignRecipientInput[]
): string[] {
  const missing: string[] = [];
  for (const key of templateKeys) {
    if (defaultFieldsProvidesKey(defaultFields, key)) continue;
    const allHave = recipients.every((r) => recipientFieldsProvidesKey(r.fields, key));
    if (allHave) continue;
    missing.push(key);
  }
  return missing;
}

function parseDefaultFieldsJson(raw: string): { ok: true; fields?: Record<string, string> } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, fields: undefined };
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { ok: false, error: 'defaultFields deve ser um objeto JSON (ex: {"1":"João"}).' };
    }
    return { ok: true, fields: parsed as Record<string, string> };
  } catch {
    return { ok: false, error: 'JSON inválido em variáveis padrão.' };
  }
}

function defaultFieldsProvidesKey(df: Record<string, string> | undefined, key: string): boolean {
  if (!df) return false;
  if (Object.prototype.hasOwnProperty.call(df, key) && String(df[key] ?? '').trim() !== '') {
    return true;
  }
  const kn = key.trim().toLowerCase();
  for (const [k, val] of Object.entries(df)) {
    if (k.trim().toLowerCase() === kn && String(val ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
}

/**
 * Para chaves de payload de botão, o valor padrão é o próprio nome da chave
 * (usado como identificador no webhook quando o cliente clica).
 */
function injectButtonPayloadDefaults(
  defaultFields: Record<string, string> | undefined,
  templateKeys: string[],
  classifiedVars: ClassifiedTemplateVariable[]
): Record<string, string> | undefined {
  const buttonKeys = classifiedVars
    .filter((v) => templateVariableDisplayGroup(v) === 'button')
    .map((v) => v.key);
  if (buttonKeys.length === 0) return defaultFields;

  const result = { ...(defaultFields ?? {}) };
  let changed = false;
  for (const key of buttonKeys) {
    if (!defaultFieldsProvidesKey(result, key)) {
      result[key] = key;
      changed = true;
    }
  }
  return changed ? result : defaultFields;
}

function csvColumnCoversKey(columns: string[], key: string): boolean {
  const toMatch = new Set<string>([
    normalizeTemplateOrColumnKey(key),
    ...csvColumnAliasesForTemplateKey(key).map((a) => normalizeTemplateOrColumnKey(a)),
  ]);
  return columns.some((c) => toMatch.has(normalizeTemplateOrColumnKey(c)));
}

/** Chaves do template sem valor vindo do JSON padrão nem de coluna no CSV (quando columns é null, só JSON conta). */
function templateKeysMissingCoverage(
  templateKeys: string[],
  defaultFields: Record<string, string> | undefined,
  csvColumns: string[] | null
): string[] {
  const missing: string[] = [];
  for (const key of templateKeys) {
    if (defaultFieldsProvidesKey(defaultFields, key)) continue;
    if (csvColumns && csvColumnCoversKey(csvColumns, key)) continue;
    missing.push(key);
  }
  return missing;
}

const WhatsAppBatchesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientsPrefillConsumed = useRef(false);
  const variableKeysWarningSigRef = useRef<string | null>(null);
  const { showSuccess, showError, showWarning } = useToast();
  const prevStatusesRef = useRef<Record<string, string>>({});
  const [campaigns, setCampaigns] = useState<ZenviaCampaignListItem[]>([]);
  const [templates, setTemplates] = useState<ZenviaTemplateListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detail, setDetail] = useState<ZenviaCampaignDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recipients, setRecipients] = useState<ZenviaCampaignRecipientsPage | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const recipientPageSize = 25;

  const [listFilter, setListFilter] = useState<ZenviaCampaignListFilter>({});
  const [exportingList, setExportingList] = useState(false);

  const [recipientsFilter, setRecipientsFilter] = useState<ZenviaCampaignRecipientsFilter>({});
  const [exportingRecipients, setExportingRecipients] = useState(false);
  const [metrics, setMetrics] = useState<ZenviaCampaignMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(new Set());
  const [showRecreateModal, setShowRecreateModal] = useState(false);
  const [recreateSource, setRecreateSource] = useState<ZenviaRecreateSource>('FailedOnly');
  const [recreateName, setRecreateName] = useState('');
  const [recreateAutoStart, setRecreateAutoStart] = useState(false);
  const [recreateSkipPatterns, setRecreateSkipPatterns] = useState('');
  const [recreating, setRecreating] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: '',
    defaultFieldsJson: '',
    phonesText: '',
  });

  const [createRecipientsTab, setCreateRecipientsTab] = useState<CreateRecipientsTab>('phones');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewResponse | null>(null);
  const [csvPreviewLoading, setCsvPreviewLoading] = useState(false);
  const [csvPhoneColumn, setCsvPhoneColumn] = useState('');
  /** Mapeamento variável_do_template → coluna_do_csv. Chave ausente = variável vem do JSON padrão. */
  const [csvColumnMapper, setCsvColumnMapper] = useState<Record<string, string>>({});

  /** Chaves de `fields` inferidas do template (GET Zenvia, mesmo critério do assistente em Templates). */
  const [templateVarKeys, setTemplateVarKeys] = useState<string[]>([]);
  const [templateVarKeysLoading, setTemplateVarKeysLoading] = useState(false);
  /** Resposta bruta do template (para `examples` no CSV de exemplo). */
  const [templateRaw, setTemplateRaw] = useState<unknown>(null);
  const [prefillClients, setPrefillClients] = useState<ClientListResponse[]>([]);
  const [recipientSource, setRecipientSource] = useState<RecipientSource>('manual');
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  const loadData = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [campRes, tpl] = await Promise.all([
        whatsAppApiService.listZenviaCampaigns(page, pageSize, listFilter),
        whatsAppApiService.getZenviaTemplates(),
      ]);
      setCampaigns(campRes.items);
      setTotal(campRes.total);
      setTemplates(tpl);
    } catch (err) {
      if (!silent) setError(getWhatsAppApiErrorMessage(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, listFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showCreateModal) {
      setTemplateVarKeys([]);
      setTemplateVarKeysLoading(false);
      setTemplateRaw(null);
      return;
    }
    const id = formData.templateId.trim();
    if (!id) {
      setTemplateVarKeys([]);
      setTemplateVarKeysLoading(false);
      setTemplateRaw(null);
      return;
    }
    let cancelled = false;
    setTemplateVarKeysLoading(true);
    void whatsAppApiService
      .getTemplateById(id)
      .then((raw) => {
        if (cancelled) return;
        setTemplateRaw(raw);
        setTemplateVarKeys(extractZenviaTemplateVariableKeys(raw));
      })
      .catch(() => {
        if (!cancelled) {
          setTemplateVarKeys([]);
          setTemplateRaw(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTemplateVarKeysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreateModal, formData.templateId]);

  useEffect(() => {
    if (!showCreateModal) {
      variableKeysWarningSigRef.current = null;
    }
  }, [showCreateModal]);

  useEffect(() => {
    if (!showCreateModal || templateVarKeysLoading) return;
    const tid = formData.templateId.trim();
    if (!tid || templateVarKeys.length === 0) return;
    const sig = `${tid}\u001f${templateVarKeys.join('\u001f')}`;
    if (variableKeysWarningSigRef.current === sig) return;
    variableKeysWarningSigRef.current = sig;
    const preview =
      templateVarKeys.length <= 6
        ? templateVarKeys.join(', ')
        : `${templateVarKeys.slice(0, 5).join(', ')}… (+${templateVarKeys.length - 5})`;
    showWarning(
      `Este template usa variáveis (${preview}). Preencha o JSON padrão e/ou colunas no CSV com esses nomes; caso contrário o disparo sai sem dados personalizados.`
    );
  }, [
    showCreateModal,
    formData.templateId,
    templateVarKeys,
    templateVarKeysLoading,
    showWarning,
  ]);

  useEffect(() => {
    if (clientsPrefillConsumed.current) return;
    const st = location.state as BatchesLocationState | null;
    if (st?.prefillClients?.length) {
      clientsPrefillConsumed.current = true;
      setPrefillClients(st.prefillClients);
      setRecipientSource('fromClients');
      setCreateRecipientsTab('phones');
      if (st.phones?.length) {
        setFormData((prev) => ({
          ...prev,
          phonesText: st.phones!.join('\n'),
        }));
      }
      setShowCreateModal(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
      return;
    }
    if (st?.phones?.length) {
      clientsPrefillConsumed.current = true;
      setCreateRecipientsTab('phones');
      setFormData((prev) => ({
        ...prev,
        phonesText: st.phones!.join('\n'),
      }));
      setShowCreateModal(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const derivedRecipientsFromClients = useMemo((): ZenviaCampaignRecipientInput[] | null => {
    if (recipientSource !== 'fromClients' || prefillClients.length === 0) return null;
    if (!formData.templateId.trim() || templateVarKeysLoading) return null;
    const list: ZenviaCampaignRecipientInput[] = [];
    for (const c of prefillClients) {
      const phone = normalizeCampaignPhoneDigits(c.telefone1 ?? '');
      if (phone.length < 10) continue;
      const metadata = buildClienteWhatsAppMetadataFields(c);
      const templateMapped = mapClientToTemplateFields(c, templateVarKeys);
      const fields: Record<string, string> = {};
      for (const [k, v] of Object.entries(metadata)) {
        if (String(v).trim()) fields[k] = v.trim();
      }
      for (const [k, v] of Object.entries(templateMapped)) {
        if (String(v).trim()) fields[k] = v.trim();
      }
      list.push({
        phone,
        fields: Object.keys(fields).length > 0 ? fields : undefined,
      });
    }
    return list.length > 0 ? list : null;
  }, [
    recipientSource,
    prefillClients,
    formData.templateId,
    templateVarKeys,
    templateVarKeysLoading,
  ]);

  useEffect(() => {
    if (recipientSource !== 'fromClients' || !derivedRecipientsFromClients?.length) return;
    const text = derivedRecipientsFromClients.map((r) => r.phone).join('\n');
    setFormData((prev) => (prev.phonesText === text ? prev : { ...prev, phonesText: text }));
  }, [recipientSource, derivedRecipientsFromClients]);

  useEffect(() => {
    const prev = prevStatusesRef.current;
    for (const c of campaigns) {
      const p = prev[c.id];
      const wasActive = p === 'Queued' || p === 'Running';
      const nowTerminal =
        c.status === 'Completed' || c.status === 'Cancelled' || c.status === 'Failed';
      if (wasActive && nowTerminal) {
        if (c.status === 'Completed') {
          showSuccess(
            `Campanha "${c.name}": concluída — ${c.sentCount} enviados, ${c.failedCount} falhas.`
          );
        } else if (c.status === 'Cancelled') {
          showWarning(
            `Campanha "${c.name}" cancelada. Um job já em execução pode ainda enviar para alguns números deste lote.`
          );
        } else {
          showError(`Campanha "${c.name}" finalizada com status ${c.status}.`);
        }
      }
    }
    const next: Record<string, string> = {};
    for (const co of campaigns) next[co.id] = co.status;
    prevStatusesRef.current = next;
  }, [campaigns, showSuccess, showWarning, showError]);

  useEffect(() => {
    const hasActive = campaigns.some((c) => c.status === 'Queued' || c.status === 'Running');
    if (!hasActive) return undefined;
    const t = window.setInterval(() => {
      void loadData({ silent: true });
    }, 4000);
    return () => window.clearInterval(t);
  }, [campaigns, loadData]);

  const parsePhones = (text: string): string[] =>
    text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

  const resetCreateModalExtras = () => {
    setCreateRecipientsTab('phones');
    setCsvFile(null);
    setCsvPreview(null);
    setCsvPhoneColumn('');
    setCsvColumnMapper({});
  };

  /** Auto-detecta qual coluna CSV corresponde a cada variável do template (case-insensitive + aliases). */
  const autoDetectColumnMapper = useCallback(
    (columns: string[], phoneCol: string): Record<string, string> => {
      if (templateVarKeys.length === 0 || columns.length === 0) return {};

      const availableColumns = columns.filter(
        (c) => c.toLowerCase() !== phoneCol.toLowerCase()
      );
      const mapper: Record<string, string> = {};
      const usedColumns = new Set<string>();

      for (const varKey of templateVarKeys) {
        const varNorm = normalizeTemplateOrColumnKey(varKey);
        const aliases = new Set([
          varNorm,
          ...csvColumnAliasesForTemplateKey(varKey).map(normalizeTemplateOrColumnKey),
        ]);
        for (const col of availableColumns) {
          if (usedColumns.has(col)) continue;
          if (aliases.has(normalizeTemplateOrColumnKey(col))) {
            mapper[varKey] = col;
            usedColumns.add(col);
            break;
          }
        }
      }
      return mapper;
    },
    [templateVarKeys]
  );

  const resetRecipientPrefill = () => {
    setPrefillClients([]);
    setRecipientSource('manual');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetRecipientPrefill();
    setClientPickerOpen(false);
  };

  const openCreateModalClean = () => {
    setFormData({
      name: '',
      description: '',
      templateId: '',
      defaultFieldsJson: '',
      phonesText: '',
    });
    resetCreateModalExtras();
    resetRecipientPrefill();
    setShowCreateModal(true);
  };

  const keyClassification = useMemo(() => classifyTemplateKeys(templateVarKeys), [templateVarKeys]);

  const classifiedTemplateVars = useMemo(
    () => classifyZenviaTemplateVariableKeys(templateRaw),
    [templateRaw]
  );

  const templateVariableBuckets = useMemo(() => {
    const message: ClassifiedTemplateVariable[] = [];
    const button: ClassifiedTemplateVariable[] = [];
    const metadata: ClassifiedTemplateVariable[] = [];
    for (const it of classifiedTemplateVars) {
      const g = templateVariableDisplayGroup(it);
      if (g === 'message') message.push(it);
      else if (g === 'button') button.push(it);
      else metadata.push(it);
    }
    return { message, button, metadata };
  }, [classifiedTemplateVars]);

  /** GET /templates/{id} costuma trazer senderId mesmo quando a lista omite. */
  const effectiveTemplateSenderId = useMemo(() => {
    const fromDetail = extractZenviaTemplateSenderId(templateRaw);
    if (fromDetail) return fromDetail;
    const tid = formData.templateId.trim();
    if (!tid) return undefined;
    return templates.find((t) => t.id === tid)?.senderId;
  }, [templateRaw, formData.templateId, templates]);

  const renderTemplateVariableRow = (
    it: ClassifiedTemplateVariable,
    variant: 'message' | 'button' | 'metadata'
  ) => {
    const sharedTextAndButton =
      it.origins.includes('button_payload') &&
      (it.origins.includes('body_text') || it.origins.includes('header_text'));
    const noClient = variant === 'button' || isLikelyButtonPayloadKey(it.key);
    let clientHint: string;
    if (variant === 'button') {
      clientHint =
        'Não preenche pelo cadastro — é o valor de payload técnico do botão (não é o rótulo que o cliente vê). Use JSON padrão ou coluna no CSV.';
    } else if (variant === 'metadata') {
      clientHint = noClient
        ? 'Informe no JSON ou CSV com o nome exato da chave.'
        : 'Consta no registro ou exemplos da API: JSON, CSV ou cadastro de clientes se a chave for reconhecida (ex.: EMPRESA, CNPJ).';
    } else {
      clientHint = noClient
        ? 'Não preenche pelo cadastro de clientes — use JSON padrão ou coluna no CSV (veja o CSV de exemplo).'
        : 'Pode ser preenchida pelo cadastro quando a chave for reconhecida (CNPJ, razão social, empresa, etc.), além de JSON ou CSV.';
    }

    return (
      <List.Item key={it.key}>
        <List.Content>
          <List.Header
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              rowGap: 6,
            }}
          >
            <code style={{ fontSize: '1.02em' }}>{it.key}</code>
            {it.origins.map((origin) => (
              <Popup
                key={origin}
                wide
                hoverable
                trigger={
                  <Label as="span" size="small" color="teal" style={{ cursor: 'help' }}>
                    {templateVariableOriginBadgePt[origin]}
                  </Label>
                }
                content={<p style={{ margin: 0, maxWidth: 360 }}>{templateVariableOriginHelpPt[origin]}</p>}
              />
            ))}
          </List.Header>
          <List.Description style={{ marginTop: 8, fontSize: '0.9em', color: '#444' }}>
            {noClient ? (
              <>
                <Icon name="info circle" color="grey" /> {clientHint}
              </>
            ) : (
              <>
                <Icon name="user outline" color="blue" /> {clientHint}
              </>
            )}
          </List.Description>
          {sharedTextAndButton && (
            <div style={{ marginTop: 8, fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}>
              Usada no texto da mensagem e no payload de um botão: <strong>um único valor</strong> no JSON/CSV serve para
              os dois.
            </div>
          )}
        </List.Content>
      </List.Item>
    );
  };

  const handleAnalyzeCsv = async () => {
    if (!csvFile) {
      setError('Selecione um arquivo CSV.');
      return;
    }
    setCsvPreviewLoading(true);
    setError(null);
    try {
      const p = await whatsAppApiService.previewZenviaCampaignCsv(csvFile);
      setCsvPreview(p);
      setCsvPhoneColumn(p.phoneColumnDetected);
      setCsvColumnMapper(autoDetectColumnMapper(p.columns, p.phoneColumnDetected));
    } catch (err) {
      setCsvPreview(null);
      setCsvColumnMapper({});
      const msg = getWhatsAppApiErrorMessage(err);
      setError(msg);
      showError(msg);
    } finally {
      setCsvPreviewLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!formData.name.trim() || !formData.templateId.trim()) {
      setError('Nome e template são obrigatórios.');
      return;
    }

    const dfParsed = parseDefaultFieldsJson(formData.defaultFieldsJson);
    if (!dfParsed.ok) {
      setError(dfParsed.error);
      return;
    }
    const defaultFields = injectButtonPayloadDefaults(dfParsed.fields, templateVarKeys, classifiedTemplateVars);

    if (formData.templateId.trim() && templateVarKeysLoading) {
      setError('Aguarde o carregamento das variáveis do template.');
      return;
    }

    if (templateVarKeys.length > 0) {
      if (createRecipientsTab === 'csv') {
        if (!csvPreview) {
          const msg =
            'Este template tem variáveis dinâmicas. Clique em Analisar no CSV para conferir se existem colunas com o mesmo nome das chaves, ou preencha o JSON padrão com todos os valores.';
          setError(msg);
          showError(msg);
          return;
        }
        const mappedKeys = Object.keys(csvColumnMapper).filter((k) => csvColumnMapper[k]?.trim());
        const missing = templateVarKeys.filter(
          (key) => !defaultFieldsProvidesKey(defaultFields, key) && !mappedKeys.includes(key)
        );
        if (missing.length > 0) {
          const msg = `Faltam dados para as variáveis do template: ${missing.join(', ')}. Mapeie uma coluna do CSV para cada variável, ou preencha o JSON padrão.`;
          setError(msg);
          showError(msg);
          return;
        }
      } else {
        const structured =
          recipientSource === 'fromClients' &&
          derivedRecipientsFromClients &&
          derivedRecipientsFromClients.length > 0;
        const missing = structured
          ? templateKeysMissingCoverageStructured(
              templateVarKeys,
              defaultFields,
              derivedRecipientsFromClients
            )
          : templateKeysMissingCoverage(templateVarKeys, defaultFields, null);
        if (missing.length > 0) {
          const msg = structured
            ? `Faltam dados para as variáveis do template: ${missing.join(', ')}. Com clientes do cadastro, preencha o JSON padrão (útil para payloads de botão) ou ajuste colunas no CSV. Baixe o CSV de exemplo para ver os nomes exatos das chaves.`
            : `Este template exige variáveis: ${missing.join(', ')}. Preencha o JSON padrão com valores para cada chave (não dá para enviar só com a lista de telefones sem JSON).`;
          setError(msg);
          showError(msg);
          return;
        }
      }
    }

    if (createRecipientsTab === 'csv') {
      if (!csvFile) {
        setError('Selecione um arquivo CSV.');
        return;
      }

      setCreating(true);
      setError(null);
      try {
        const activeMapper: Record<string, string> = {};
        for (const [tplKey, csvCol] of Object.entries(csvColumnMapper)) {
          if (csvCol?.trim()) activeMapper[tplKey] = csvCol.trim();
        }
        const res = await whatsAppApiService.uploadZenviaCampaignCsv(csvFile, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          templateId: formData.templateId.trim(),
          defaultFields,
          phoneColumn: csvPhoneColumn.trim() || undefined,
          columnMapper: Object.keys(activeMapper).length > 0 ? activeMapper : undefined,
        });
        closeCreateModal();
        setFormData({
          name: '',
          description: '',
          templateId: '',
          defaultFieldsJson: '',
          phonesText: '',
        });
        resetCreateModalExtras();
        setPage(1);
        await loadData();
        showSuccess(
          `Campanha criada em rascunho (${res.recipientCount} destinatários no CSV). Use ▶ Iniciar para enfileirar o envio.`
        );
      } catch (err) {
        const msg = getWhatsAppApiErrorMessage(err);
        setError(msg);
        showError(msg);
      } finally {
        setCreating(false);
      }
      return;
    }

    if (
      createRecipientsTab === 'phones' &&
      recipientSource === 'fromClients' &&
      prefillClients.length > 0 &&
      (!derivedRecipientsFromClients || derivedRecipientsFromClients.length === 0)
    ) {
      const msg =
        'Não há destinatários válidos a partir dos clientes selecionados: confira telefone1 no cadastro (mín. 10 dígitos), selecione o template e aguarde o carregamento das variáveis.';
      setError(msg);
      showError(msg);
      return;
    }

    const useStructuredRecipients =
      recipientSource === 'fromClients' &&
      derivedRecipientsFromClients &&
      derivedRecipientsFromClients.length > 0;

    let recipientsPayload: ZenviaCampaignRecipientInput[];
    if (useStructuredRecipients) {
      recipientsPayload = derivedRecipientsFromClients;
    } else {
      const phones = parsePhones(formData.phonesText);
      if (phones.length === 0) {
        setError('Informe ao menos um telefone (um por linha).');
        return;
      }
      recipientsPayload = phones.map((phone) => ({ phone }));
    }

    setCreating(true);
    setError(null);
    try {
      await whatsAppApiService.createZenviaCampaign({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        templateId: formData.templateId.trim(),
        defaultFields,
        recipients: recipientsPayload,
      });
      closeCreateModal();
      setFormData({
        name: '',
        description: '',
        templateId: '',
        defaultFieldsJson: '',
        phonesText: '',
      });
      resetCreateModalExtras();
      setPage(1);
      await loadData();
      showSuccess('Campanha criada em rascunho. Use ▶ Iniciar para enfileirar o envio.');
    } catch (err) {
      const msg = getWhatsAppApiErrorMessage(err);
      setError(msg);
      showError(msg);
    } finally {
      setCreating(false);
    }
  };

  const loadRecipients = async (
    campaignId: string,
    page: number,
    filter?: ZenviaCampaignRecipientsFilter
  ) => {
    setRecipientsLoading(true);
    try {
      const r = await whatsAppApiService.getZenviaCampaignRecipients(
        campaignId,
        page,
        recipientPageSize,
        filter ?? recipientsFilter
      );
      setRecipients(r);
    } catch (err) {
      setError(getWhatsAppApiErrorMessage(err));
      setRecipients(null);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const loadMetrics = async (campaignId: string) => {
    setMetricsLoading(true);
    try {
      const m = await whatsAppApiService.getZenviaCampaignMetrics(campaignId);
      setMetrics(m);
    } catch (err) {
      setMetrics(null);
      setError(getWhatsAppApiErrorMessage(err));
    } finally {
      setMetricsLoading(false);
    }
  };

  const openDetail = async (id: string) => {
    setShowDetailModal(true);
    setDetail(null);
    setRecipients(null);
    setMetrics(null);
    setSelectedRecipientIds(new Set());
    setRecipientsFilter({});
    setDetailLoading(true);
    try {
      const d = await whatsAppApiService.getZenviaCampaign(id);
      setDetail(d);
    } catch (err) {
      setError(getWhatsAppApiErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
    await loadRecipients(id, 1, {});
    void loadMetrics(id);
  };

  const refreshDetail = async (id: string) => {
    try {
      const d = await whatsAppApiService.getZenviaCampaign(id);
      setDetail(d);
      const rp = recipients?.page ?? 1;
      await loadRecipients(id, rp);
      void loadMetrics(id);
    } catch (err) {
      setError(getWhatsAppApiErrorMessage(err));
    }
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCampaigns = async () => {
    setExportingList(true);
    try {
      const blob = await whatsAppApiService.exportZenviaCampaignsXlsx(listFilter);
      downloadBlob(blob, `campanhas-zenvia-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.xlsx`);
      showSuccess('Lista de campanhas exportada.');
    } catch (err) {
      showError(getWhatsAppApiErrorMessage(err));
    } finally {
      setExportingList(false);
    }
  };

  const handleExportRecipients = async () => {
    if (!detail) return;
    setExportingRecipients(true);
    try {
      const blob = await whatsAppApiService.exportZenviaCampaignRecipientsXlsx(detail.id, recipientsFilter);
      downloadBlob(
        blob,
        `campanha-${detail.id}-destinatarios-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.xlsx`
      );
      showSuccess('Destinatarios exportados.');
    } catch (err) {
      showError(getWhatsAppApiErrorMessage(err));
    } finally {
      setExportingRecipients(false);
    }
  };

  const toggleSelectRecipient = (id: string) => {
    setSelectedRecipientIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllInPage = () => {
    if (!recipients) return;
    setSelectedRecipientIds((prev) => {
      const next = new Set(prev);
      const allSelected = recipients.items.every((r) => next.has(r.id));
      if (allSelected) {
        recipients.items.forEach((r) => next.delete(r.id));
      } else {
        recipients.items.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const openRecreateModal = (defaultSource: ZenviaRecreateSource = 'FailedOnly') => {
    setRecreateSource(defaultSource);
    setRecreateName(detail ? `${detail.name} (Refazer)` : '');
    setRecreateAutoStart(false);
    setRecreateSkipPatterns('');
    setShowRecreateModal(true);
  };

  const handleRecreate = async () => {
    if (!detail) return;
    setRecreating(true);
    try {
      const skipPatterns = recreateSkipPatterns
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body: ZenviaRecreateCampaignRequest = {
        name: recreateName.trim() || undefined,
        source: recreateSource,
        autoStart: recreateAutoStart,
        skipErrorPatterns: skipPatterns.length > 0 ? skipPatterns : undefined,
      };
      if (recreateSource === 'SelectedIds') {
        if (selectedRecipientIds.size === 0) {
          showError('Selecione pelo menos um destinatario.');
          setRecreating(false);
          return;
        }
        body.recipientIds = Array.from(selectedRecipientIds);
      }
      const res = await whatsAppApiService.recreateZenviaCampaign(detail.id, body);
      showSuccess(
        res.started
          ? `Nova campanha criada e iniciada (id ${res.id.slice(0, 8)}…).`
          : `Nova campanha criada como rascunho (id ${res.id.slice(0, 8)}…).`
      );
      setShowRecreateModal(false);
      setShowDetailModal(false);
      await loadData();
    } catch (err) {
      showError(getWhatsAppApiErrorMessage(err));
    } finally {
      setRecreating(false);
    }
  };

  const handleStart = async (id: string) => {
    setError(null);
    try {
      const res = await whatsAppApiService.startZenviaCampaign(id);
      showSuccess(res.message ?? 'Campanha enfileirada. O envio começa quando o Hangfire pegar o job (fila whatsapp).');
      await loadData();
      if (detail?.id === id) await refreshDetail(id);
    } catch (err) {
      const msg = getWhatsAppApiErrorMessage(err);
      setError(msg);
      showError(msg);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancelar esta campanha?')) return;
    setError(null);
    try {
      await whatsAppApiService.cancelZenviaCampaign(id);
      await loadData();
      if (detail?.id === id) await refreshDetail(id);
    } catch (err) {
      const msg = getWhatsAppApiErrorMessage(err);
      setError(msg);
      showError(msg);
    }
  };

  const [retryingAll, setRetryingAll] = useState(false);
  const [retryingRecipientId, setRetryingRecipientId] = useState<string | null>(null);

  const handleRetryFailed = async (campaignId: string) => {
    if (!window.confirm('Reenviar todos os destinatários com falha?')) return;
    setError(null);
    setRetryingAll(true);
    try {
      const res = await whatsAppApiService.retryFailedZenviaCampaign(campaignId);
      showSuccess(res.message);
      await loadData();
      if (detail?.id === campaignId) await refreshDetail(campaignId);
    } catch (err) {
      const msg = getWhatsAppApiErrorMessage(err);
      setError(msg);
      showError(msg);
    } finally {
      setRetryingAll(false);
    }
  };

  const handleRetryOne = async (campaignId: string, recipientId: string) => {
    setError(null);
    setRetryingRecipientId(recipientId);
    try {
      const res = await whatsAppApiService.retryOneZenviaCampaignRecipient(campaignId, recipientId);
      showSuccess(res.message);
      if (detail?.id === campaignId) await refreshDetail(campaignId);
    } catch (err) {
      const msg = getWhatsAppApiErrorMessage(err);
      showError(msg);
    } finally {
      setRetryingRecipientId(null);
    }
  };

  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icon name="paper plane" />
          Campanhas Zenvia (disparo)
        </h1>
        <Button primary icon labelPosition="left" onClick={openCreateModalClean}>
          <Icon name="plus" />
          Nova campanha
        </Button>
      </div>

      <Message info>
        <Message.Header>Campanhas internas (Gettel)</Message.Header>
        <p>
          Ao clicar em <strong>Iniciar</strong>, a campanha sai de <code>Draft</code>, passa para <code>Queued</code> e
          entra na <strong>fila Hangfire &quot;whatsapp&quot;</strong>. Não há delay fixo: o envio começa quando um worker
          livre pegar o job (em dev, com poucos workers, pode levar alguns segundos). Depois o status vira{' '}
          <code>Running</code> e, ao terminar, <code>Completed</code>. No detalhe da campanha você vê cada número:{' '}
          <code>Pending</code>/<code>Sent</code>/<code>Failed</code>, id da mensagem na Zenvia (quando sucesso), erro e
          horário processado. A lista de campanhas é atualizada automaticamente enquanto houver campanha em fila ou em
          execução.
        </p>
        <p style={{ marginTop: '0.5em' }}>
          Formato: um telefone por linha. Opcionalmente use JSON em <strong>variáveis padrão</strong> (ex.:{' '}
          <code>{`{"1":"Promoção"}`}</code>).
        </p>
      </Message>

      {error && (
        <Message error onDismiss={() => setError(null)}>
          <Message.Header>Erro</Message.Header>
          <p style={{ whiteSpace: 'pre-wrap' }}>{error}</p>
        </Message>
      )}

      <Segment style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
          <Form.Field style={{ minWidth: 180, marginBottom: 0 }}>
            <label style={{ fontSize: '0.85em' }}>Buscar (nome / template)</label>
            <input
              type="text"
              value={listFilter.search ?? ''}
              onChange={(e) => setListFilter((f) => ({ ...f, search: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  void loadData();
                }
              }}
              placeholder="ex.: black friday"
              style={{ padding: '6px 8px', width: '100%' }}
            />
          </Form.Field>
          <Form.Field style={{ minWidth: 140, marginBottom: 0 }}>
            <label style={{ fontSize: '0.85em' }}>Status</label>
            <select
              value={listFilter.status ?? ''}
              onChange={(e) => {
                setPage(1);
                setListFilter((f) => ({ ...f, status: e.target.value || undefined }));
              }}
              style={{ padding: '6px 8px', width: '100%' }}
            >
              <option value="">Todos</option>
              <option value="Draft">Draft</option>
              <option value="Queued">Queued</option>
              <option value="Running">Running</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </Form.Field>
          <Form.Field style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.85em' }}>Criada de</label>
            <input
              type="date"
              value={listFilter.createdFrom ?? ''}
              onChange={(e) => {
                setPage(1);
                setListFilter((f) => ({ ...f, createdFrom: e.target.value || undefined }));
              }}
              style={{ padding: '6px 8px' }}
            />
          </Form.Field>
          <Form.Field style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.85em' }}>até</label>
            <input
              type="date"
              value={listFilter.createdTo ?? ''}
              onChange={(e) => {
                setPage(1);
                setListFilter((f) => ({ ...f, createdTo: e.target.value || undefined }));
              }}
              style={{ padding: '6px 8px' }}
            />
          </Form.Field>
          <Button
            size="small"
            primary
            icon
            labelPosition="left"
            onClick={() => {
              setPage(1);
              void loadData();
            }}
          >
            <Icon name="filter" />
            Filtrar
          </Button>
          <Button
            size="small"
            basic
            onClick={() => {
              setListFilter({});
              setPage(1);
            }}
          >
            Limpar
          </Button>
          <Button
            size="small"
            color="green"
            icon
            labelPosition="left"
            loading={exportingList}
            onClick={() => void handleExportCampaigns()}
          >
            <Icon name="file excel" />
            Exportar Excel
          </Button>
        </div>
      </Segment>

      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Anterior
        </Button>
        <span>
          Página {page} / {maxPage} ({total} campanhas)
        </span>
        <Button size="small" disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}>
          Próxima
        </Button>
        <Button size="small" icon onClick={() => loadData()}>
          <Icon name="refresh" />
        </Button>
      </div>

      {loading ? (
        <Loader active size="large" content="Carregando campanhas..." />
      ) : (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name="list" />
              Campanhas
            </Card.Header>
          </Card.Content>
          <Card.Content>
            {!campaigns.length ? (
              <Segment placeholder>
                <Header icon>
                  <Icon name="paper plane" />
                  Nenhuma campanha
                </Header>
                <Button primary onClick={openCreateModalClean}>
                  Criar campanha
                </Button>
              </Segment>
            ) : (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Nome</Table.HeaderCell>
                    <Table.HeaderCell>Template ID</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Progresso</Table.HeaderCell>
                    <Table.HeaderCell>Pend.</Table.HeaderCell>
                    <Table.HeaderCell>Enviados</Table.HeaderCell>
                    <Table.HeaderCell>Falhas</Table.HeaderCell>
                    <Table.HeaderCell>Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {campaigns.map((c) => (
                    <Table.Row key={c.id}>
                      <Table.Cell>
                        <strong>{c.name}</strong>
                      </Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: '0.85em' }}>{c.templateId}</code>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={statusColor(c.status)}>{c.status}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Progress
                          percent={progressPercent(c)}
                          size="small"
                          color={progressPercent(c) === 100 ? 'green' : 'blue'}
                        />
                      </Table.Cell>
                      <Table.Cell>{c.pendingCount}</Table.Cell>
                      <Table.Cell>{c.sentCount}</Table.Cell>
                      <Table.Cell>{c.failedCount}</Table.Cell>
                      <Table.Cell>
                        <Button.Group size="mini">
                          <Button icon="info" title="Detalhe" onClick={() => openDetail(c.id)} />
                          {c.status === 'Draft' && (
                            <Button
                              positive
                              icon="play"
                              title="Iniciar"
                              onClick={() => handleStart(c.id)}
                            />
                          )}
                          {c.status !== 'Completed' && c.status !== 'Cancelled' && c.status !== 'Failed' && (
                            <Button color="red" icon="cancel" title="Cancelar" onClick={() => handleCancel(c.id)} />
                          )}
                          {c.failedCount > 0 && (
                            <Button
                              color="orange"
                              icon="redo"
                              title={`Reenviar ${c.failedCount} falha(s)`}
                              loading={retryingAll}
                              onClick={() => handleRetryFailed(c.id)}
                            />
                          )}
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Card.Content>
        </Card>
      )}

      <Modal open={showCreateModal} onClose={closeCreateModal} size="large">
        <Modal.Header>
          <Icon name="plus" />
          Nova campanha
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Message info style={{ marginBottom: '16px' }}>
              <Message.Header>Como montar esta campanha</Message.Header>
              <List ordered relaxed>
                <List.Item>
                  Escolha o <strong>template</strong> antes dos destinatários ou do CSV — as variáveis dependem dele.
                </List.Item>
                <List.Item>
                  Use o <strong>JSON padrão</strong> para o mesmo valor em todos (incluindo payloads de botão quando não
                  forem por linha).
                </List.Item>
                <List.Item>
                  <strong>Telefones</strong> (lista ou clientes do cadastro) ou <strong>CSV</strong> — baixe o modelo
                  após escolher o template e use <strong>Analisar</strong> antes de criar.
                </List.Item>
              </List>
            </Message>
            <Step.Group size="tiny" fluid style={{ marginBottom: '16px' }}>
              <Step active title="Template" description="Modelo Zenvia" />
              <Step active title="Variáveis" description="JSON opcional" />
              <Step active title="Destinatários" description="Lista / clientes / CSV" />
            </Step.Group>
            <Grid columns={2}>
              <Grid.Column>
                <Form.Field required>
                  <label>Nome</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Descrição</label>
                  <input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid>
            <Form.Field required>
              <label>Passo 1 — Template</label>
              <p style={{ margin: '0 0 6px', fontSize: '0.9em', color: '#555' }}>
                Escolha o modelo antes de destinatários ou CSV; as variáveis listadas abaixo vêm deste template.
              </p>
              <select
                value={formData.templateId}
                onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Selecione…</option>
                {templates
                  .filter((t) => t.id)
                  .map((t) => {
                    const parts: string[] = [t.name];
                    if (t.senderId) parts.push(`sender ${t.senderId}`);
                    if (t.category) parts.push(`[${t.category}]`);
                    if (t.fields && t.fields.length > 0) parts.push(`${t.fields.length} var`);
                    if (t.buttonCount) parts.push(`${t.buttonCount} btn`);
                    if (t.status && t.status !== 'APPROVED') parts.push(`⚠ ${t.status}`);
                    return (
                      <option key={t.id} value={t.id}>
                        {parts.join(' · ')}
                      </option>
                    );
                  })}
              </select>
            </Form.Field>
            {formData.templateId.trim() && !templateVarKeysLoading && (() => {
              const selTpl = templates.find((t) => t.id === formData.templateId);
              return (
                <Segment style={{ marginBottom: '12px', background: '#f9fafb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <strong style={{ fontSize: '1.05em' }}>{selTpl?.name ?? '—'}</strong>
                      <div style={{ fontSize: '0.8em', color: '#888', marginTop: 2 }}>
                        <code>{formData.templateId}</code>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      {selTpl?.category && <Label size="mini">{selTpl.category}</Label>}
                      {selTpl?.status && <Label size="mini" color={selTpl.status === 'APPROVED' ? 'green' : 'yellow'}>{selTpl.status}</Label>}
                      {templateVarKeysLoading ? (
                        <Label size="mini" basic color="grey">
                          Remetente: carregando…
                        </Label>
                      ) : effectiveTemplateSenderId ? (
                        <Label size="mini" basic color="blue">
                          Remetente (senderId): {effectiveTemplateSenderId}
                        </Label>
                      ) : (
                        <Label size="mini" basic color="orange">
                          Remetente não identificado na API
                        </Label>
                      )}
                    </div>
                  </div>

                  {selTpl?.componentSummary && (
                    <div style={{ fontSize: '0.85em', color: '#555', marginTop: 8 }}>
                      <Icon name="puzzle piece" /> {selTpl.componentSummary}
                    </div>
                  )}

                  {selTpl?.bodyText && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, fontSize: '0.88em', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflow: 'auto' }}>
                      {selTpl.bodyText.length > 300 ? `${selTpl.bodyText.slice(0, 300)}…` : selTpl.bodyText}
                    </div>
                  )}

                  {selTpl?.headerText && (
                    <div style={{ fontSize: '0.82em', color: '#666', marginTop: 6 }}>
                      <strong>Header:</strong> {selTpl.headerText}
                    </div>
                  )}

                  {selTpl?.buttonLabels && selTpl.buttonLabels.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.82em', color: '#666' }}>Botões:</span>
                      {selTpl.buttonLabels.map((bl, i) => (
                        <Label key={i} size="mini" color="violet" basic>{bl}</Label>
                      ))}
                    </div>
                  )}

                  <Divider fitted style={{ margin: '10px 0 8px' }} />
                  {templateVarKeys.length > 0 ? (
                    <div style={{ fontSize: '0.88em' }}>
                      <strong>{templateVarKeys.length}</strong> variável(ies):{' '}
                      {selTpl?.fields?.map((f) => (
                        <Label key={f} size="mini" color="teal" basic style={{ marginBottom: 2 }}>{`{{${f}}}`}</Label>
                      ))}
                      <div style={{ color: '#555', marginTop: 4 }}>
                        <strong>{keyClassification.clientFillable.length}</strong> do cadastro · <strong>{keyClassification.manualOnly.length}</strong> só JSON/CSV (ex.: payload de botão)
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.88em', color: '#555' }}>
                      Nenhuma variável — basta telefone no CSV ou na lista.
                    </div>
                  )}
                </Segment>
              );
            })()}
            <Form.Field>
              <label>Passo 2 — Variáveis do template (JSON padrão para todos)</label>
              <p style={{ margin: '0 0 8px', fontSize: '0.9em', color: '#555' }}>
                São as mesmas chaves enviadas à Zenvia no objeto <code>fields</code> do template (como no teste em{' '}
                <strong>Templates</strong>). Valores aqui valem para <strong>todos</strong> os destinatários; no CSV (ou
                por linha na API), colunas com o <strong>mesmo nome de chave</strong> sobrescrevem só aquele cliente. Se o
                template não tiver variáveis, pode deixar vazio.
              </p>
              {formData.templateId.trim() && !templateVarKeysLoading && classifiedTemplateVars.length > 0 && (
                <Segment raised style={{ marginBottom: '14px' }}>
                  <Header as="h4" style={{ marginTop: 0 }}>
                    <Icon name="list ol" />
                    O que precisa ser preenchido neste template
                  </Header>
                  <p style={{ margin: '0 0 16px', fontSize: '0.92em', color: '#333' }}>
                    Cada variável abaixo precisa de um valor no disparo. Passe o mouse nos rótulos{' '}
                    <Label size="tiny" color="teal">
                      Corpo
                    </Label>{' '}
                    <Label size="tiny" color="teal">
                      Botão
                    </Label>{' '}
                    etc. para ver de onde ela foi detectada. Algumas colunas do CSV com <strong>nome equivalente</strong>{' '}
                    (ex. <code>razao_social</code> para a variável <code>EMPRESA</code>) também são aceitas na validação.
                  </p>

                  {templateVariableBuckets.message.length > 0 && (
                    <div style={{ marginBottom: '22px' }}>
                      <Header as="h5" style={{ color: '#2185d0', marginBottom: 8 }}>
                        <Icon name="comment outline" />
                        Texto da mensagem (cabeçalho e/ou corpo)
                      </Header>
                      <p style={{ fontSize: '0.88em', color: '#555', marginBottom: 10 }}>
                        Substituem os <code>{'{{placeholders}}'}</code> no texto que o WhatsApp mostra ao cliente (parte de
                        cima ou corpo da mensagem).
                      </p>
                      <List divided relaxed>
                        {templateVariableBuckets.message.map((it) => renderTemplateVariableRow(it, 'message'))}
                      </List>
                    </div>
                  )}

                  {templateVariableBuckets.button.length > 0 && (
                    <div style={{ marginBottom: '22px' }}>
                      <Header as="h5" style={{ color: '#6435c9', marginBottom: 8 }}>
                        <Icon name="hand pointer outline" />
                        Botões (resposta rápida / payload)
                      </Header>
                      <Message size="small" info style={{ marginBottom: 12 }}>
                        Aqui não é o <strong>texto visível</strong> do botão: é o valor técnico de <strong>payload</strong>{' '}
                        que a API exige no disparo. O cliente não digita isso ao tocar no botão. Se a mesma chave também
                        aparecer no corpo/cabeçalho, ela foi listada acima em &quot;Texto da mensagem&quot; (um valor só).
                      </Message>
                      <List divided relaxed>
                        {templateVariableBuckets.button.map((it) => renderTemplateVariableRow(it, 'button'))}
                      </List>
                    </div>
                  )}

                  {templateVariableBuckets.metadata.length > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      <Header as="h5" style={{ color: '#767676', marginBottom: 8 }}>
                        <Icon name="database" />
                        Registro na API ou só nos exemplos
                      </Header>
                      <p style={{ fontSize: '0.88em', color: '#555', marginBottom: 10 }}>
                        Nomes que a plataforma lista para <code>fields</code>, sem placeholder no texto analisado aqui.
                        Use o <strong>mesmo nome</strong> no JSON ou CSV.
                      </p>
                      <List divided relaxed>
                        {templateVariableBuckets.metadata.map((it) => renderTemplateVariableRow(it, 'metadata'))}
                      </List>
                    </div>
                  )}
                </Segment>
              )}
              {formData.templateId.trim() && (
                <Segment size="small" style={{ marginBottom: '10px' }}>
                  {templateVarKeysLoading ? (
                    <span style={{ color: '#888' }}>
                      <Icon name="spinner" loading /> Carregando variáveis do template…
                    </span>
                  ) : templateVarKeys.length > 0 ? (
                    <>
                      <Header as="h5" style={{ marginTop: 0 }}>
                        Lista rápida das chaves (ordem da API)
                      </Header>
                      <p style={{ margin: '0 0 8px', fontSize: '0.88em' }}>
                        Mesmas chaves acima, em uma linha, para copiar mentalmente. No JSON e no CSV use estes nomes (ou
                        equivalentes aceitos na validação, conforme o quadro).
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                        {templateVarKeys.map((k) => (
                          <Label key={k} size="small" color={isLikelyButtonPayloadKey(k) ? 'violet' : 'blue'}>
                            {k}
                          </Label>
                        ))}
                      </div>
                      <Button
                        type="button"
                        size="small"
                        onClick={() => {
                          const skeleton: Record<string, string> = {};
                          for (const k of templateVarKeys) {
                            skeleton[k] = isLikelyButtonPayloadKey(k) ? k : '';
                          }
                          setFormData((prev) => ({
                            ...prev,
                            defaultFieldsJson: JSON.stringify(skeleton, null, 2),
                          }));
                        }}
                      >
                        <Icon name="write" />
                        Gerar JSON com essas chaves
                      </Button>
                    </>
                  ) : (
                    <span style={{ color: '#888', fontSize: '0.9em' }}>
                      Não encontramos placeholders <code>{'{{...}}'}</code> nem chaves em <code>examples</code> neste
                      template — pode ser texto fixo ou formato que a API não expõe aqui. Use o JSON manualmente (ex.:{' '}
                      <code>{'{"1":"..."}'}</code> se a Meta usar variáveis numeradas).
                    </span>
                  )}
                </Segment>
              )}
              <TextArea
                rows={4}
                placeholder='{"razao_social":"Gettel Telecom","cnpj":"00000000000100"} ou {"1":"valor"}'
                value={formData.defaultFieldsJson}
                onChange={(e) => setFormData({ ...formData, defaultFieldsJson: e.target.value })}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Field>
            <Divider />
            <Header as="h4">Passo 3 — Destinatários</Header>
            <p style={{ margin: '0 0 8px', fontSize: '0.9em', color: '#555' }}>
              Como você vai informar os números?
            </p>
            <List relaxed style={{ marginBottom: '12px' }}>
              <List.Item>
                <strong>Telefones:</strong> digite um por linha ou use <strong>Selecionar clientes</strong> (cadastro).
              </List.Item>
              <List.Item>
                <strong>CSV:</strong> após escolher o template, use <strong>Baixar CSV de exemplo</strong>, preencha e clique
                em <strong>Analisar</strong> antes de criar.
              </List.Item>
            </List>
            <Menu pointing secondary>
              <Menu.Item
                active={createRecipientsTab === 'phones'}
                onClick={() => setCreateRecipientsTab('phones')}
              >
                <Icon name="list" />
                Telefones
              </Menu.Item>
              <Menu.Item
                active={createRecipientsTab === 'csv'}
                onClick={() => setCreateRecipientsTab('csv')}
              >
                <Icon name="file excel outline" />
                Upload CSV
              </Menu.Item>
            </Menu>

            {createRecipientsTab === 'phones' && (
              <Form.Field required>
                <label>Telefones (um por linha)</label>
                <div
                  style={{
                    marginBottom: '10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    type="button"
                    icon
                    labelPosition="left"
                    onClick={() => setClientPickerOpen(true)}
                  >
                    <Icon name="users" />
                    Selecionar clientes…
                  </Button>
                  {recipientSource === 'fromClients' && prefillClients.length > 0 && (
                    <>
                      <Label color="teal">
                        {prefillClients.length} cliente(s) do cadastro
                      </Label>
                      <Button
                        type="button"
                        basic
                        size="small"
                        onClick={() => {
                          if (
                            !window.confirm(
                              'Trocar para lista manual? A seleção do cadastro será descartada (os telefones atuais permanecem no campo para você editar).'
                            )
                          ) {
                            return;
                          }
                          const lines = derivedRecipientsFromClients?.map((r) => r.phone).join('\n') ?? '';
                          setRecipientSource('manual');
                          setPrefillClients([]);
                          setFormData((prev) => ({ ...prev, phonesText: lines || prev.phonesText }));
                        }}
                      >
                        Trocar para lista manual
                      </Button>
                    </>
                  )}
                </div>
                {recipientSource === 'fromClients' && prefillClients.length > 0 && (
                  <Message info size="small" style={{ marginBottom: '10px' }}>
                    Modo: <strong>clientes do cadastro</strong>. Telefones vêm de <code>telefone1</code>; variáveis são
                    preenchidas quando o nome da chave corresponde ao cadastro (após escolher o template). Campos de
                    botão precisam do JSON ou CSV.
                  </Message>
                )}
                <TextArea
                  rows={8}
                  placeholder={'5541999999999\n5541888888888'}
                  value={formData.phonesText}
                  readOnly={Boolean(
                    recipientSource === 'fromClients' &&
                      derivedRecipientsFromClients &&
                      derivedRecipientsFromClients.length > 0
                  )}
                  onChange={(e) => {
                    if (recipientSource === 'fromClients') {
                      showWarning('Modo alterado para lista manual (telefones digitados).');
                      setRecipientSource('manual');
                      setPrefillClients([]);
                    }
                    setFormData({ ...formData, phonesText: e.target.value });
                  }}
                />
              </Form.Field>
            )}

            {createRecipientsTab === 'csv' && (
              <Segment>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                  }}
                >
                  {formData.templateId.trim() ? (
                    templateVarKeysLoading ? (
                      <Button disabled size="small" loading content="Carregando…" />
                    ) : (
                      <Popup
                        content="Arquivo com separador ; e UTF-8. Inclui phone, chaves do template (incl. botões) e colunas opcionais de metadado (cnpj, acessos, LINHA 1…3) que alimentam /cliente e /linhas no Chatwoot mesmo sem serem variáveis do template."
                        trigger={
                          <span>
                            <Button
                              type="button"
                              icon
                              labelPosition="left"
                              size="small"
                              onClick={() => {
                                const name =
                                  templates.find((x) => x.id === formData.templateId)?.name ?? 'campanha';
                                downloadExampleCsv(name, {
                                  templateKeys: templateVarKeys,
                                  examples: readTemplateExamples(templateRaw),
                                });
                              }}
                            >
                              <Icon name="download" />
                              Baixar CSV de exemplo
                            </Button>
                          </span>
                        }
                      />
                    )
                  ) : (
                    <Popup
                      content="Selecione um template primeiro."
                      trigger={
                        <span>
                          <Button disabled type="button" icon labelPosition="left" size="small">
                            <Icon name="download" />
                            Baixar CSV de exemplo
                          </Button>
                        </span>
                      }
                    />
                  )}
                  <span style={{ fontSize: '0.88em', color: '#555' }}>
                    Inclui a coluna <code>phone</code>, todas as variáveis deste template (incl. payloads de botão) e colunas opcionais de metadado (<code>cnpj</code>, <code>acessos</code>, <code>LINHA 1</code>…) que alimentam <code>/cliente</code> e <code>/linhas</code> no Chatwoot.
                  </span>
                </div>
                <p style={{ marginBottom: '12px', color: '#555' }}>
                  CSV com cabeçalho: coluna de telefone (<code>phone</code>, <code>telefone</code>, etc.) e colunas
                  extras cujo <strong>título = chave da variável do template</strong> (as mesmas exibidas acima, ex.:{' '}
                  <code>razao_social</code>, <code>cnpj</code>). Separador: vírgula ou ponto e vírgula. Máximo 10.000
                  linhas.
                </p>
                <Form.Field>
                  <label>Arquivo</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setCsvFile(f);
                      setCsvPreview(null);
                    }}
                  />
                </Form.Field>
                <Button
                  type="button"
                  onClick={() => void handleAnalyzeCsv()}
                  loading={csvPreviewLoading}
                  disabled={!csvFile}
                >
                  <Icon name="search" />
                  Analisar
                </Button>
                {csvPreview && (
                  <>
                    <Message info style={{ marginTop: '16px' }}>
                      <Message.Header>Pré-visualização</Message.Header>
                      <p>
                        <strong>{csvPreview.totalRows}</strong> destinatário(s). Coluna de telefone detectada:{' '}
                        <code>{csvPreview.phoneColumnDetected}</code>
                      </p>
                    </Message>
                    {csvPreview.warnings && csvPreview.warnings.length > 0 && (
                      <Message warning style={{ marginTop: '8px' }}>
                        <Message.Header>Pre-validacao do CSV</Message.Header>
                        <List bulleted>
                          {csvPreview.warnings.map((w, i) => (
                            <List.Item key={i}>
                              <strong>{w.code}</strong>
                              {w.column ? (
                                <>
                                  {' '}em <code>{w.column}</code>
                                </>
                              ) : null}
                              : {w.message}
                            </List.Item>
                          ))}
                        </List>
                      </Message>
                    )}
                    <Form.Field style={{ marginTop: '12px' }}>
                      <label>Coluna do telefone (ajuste se necessário)</label>
                      <select
                        value={csvPhoneColumn}
                        onChange={(e) => {
                          const newPhone = e.target.value;
                          setCsvPhoneColumn(newPhone);
                          if (csvPreview) {
                            setCsvColumnMapper(autoDetectColumnMapper(csvPreview.columns, newPhone));
                          }
                        }}
                        style={{ width: '100%', maxWidth: '400px', padding: '8px' }}
                      >
                        {csvPreview.columns.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </Form.Field>
                    {templateVarKeys.length > 0 && (
                      <>
                        <Divider style={{ margin: '18px 0 10px' }} />
                        <Header as="h5" style={{ marginBottom: 4 }}>
                          <Icon name="columns" />
                          De/Para: variáveis do template
                        </Header>
                        <p style={{ fontSize: '0.9em', color: '#555', marginBottom: '14px' }}>
                          Selecione qual coluna do seu CSV contém cada variável. Se já estiver no JSON padrão,
                          pode deixar em branco.
                        </p>
                        {templateVarKeys.map((varKey) => {
                          const mapped = csvColumnMapper[varKey]?.trim() ?? '';
                          const isBtnPayload = isLikelyButtonPayloadKey(varKey);
                          const dfResult = parseDefaultFieldsJson(formData.defaultFieldsJson);
                          const hasDefault = dfResult.ok
                            ? defaultFieldsProvidesKey(dfResult.fields, varKey)
                            : false;
                          const covered = !!mapped || hasDefault;
                          return (
                            <Form.Field key={varKey} style={{ marginBottom: '10px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                Coluna de{' '}
                                <code
                                  style={{
                                    background: '#f0f0f0',
                                    padding: '1px 5px',
                                    borderRadius: 3,
                                    fontWeight: 700,
                                  }}
                                >
                                  {varKey}
                                </code>
                                {isBtnPayload && (
                                  <Label size="mini" color="violet">
                                    botão
                                  </Label>
                                )}
                                {covered ? (
                                  <Icon name="check circle" color="green" style={{ marginLeft: 2 }} />
                                ) : (
                                  <Icon name="warning sign" color="yellow" style={{ marginLeft: 2 }} />
                                )}
                              </label>
                              <select
                                value={mapped}
                                onChange={(e) =>
                                  setCsvColumnMapper((prev) => ({ ...prev, [varKey]: e.target.value }))
                                }
                                style={{
                                  width: '100%',
                                  maxWidth: '400px',
                                  padding: '8px',
                                  borderColor: !covered ? '#fbbd08' : undefined,
                                }}
                              >
                                <option value="">
                                  {hasDefault ? '— Usar JSON padrão —' : '— Selecione a coluna —'}
                                </option>
                                {csvPreview.columns
                                  .filter((c) => c.toLowerCase() !== csvPhoneColumn.toLowerCase())
                                  .map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                              </select>
                              {!covered && (
                                <span style={{ fontSize: '0.85em', color: '#b58105', marginTop: 2, display: 'block' }}>
                                  Nenhuma coluna selecionada e sem valor no JSON padrão.
                                </span>
                              )}
                            </Form.Field>
                          );
                        })}
                      </>
                    )}
                    <Header as="h5" style={{ marginTop: '16px' }}>
                      Amostra (até 5 linhas)
                    </Header>
                    <div style={{ overflowX: 'auto' }}>
                      <Table celled compact size="small">
                        <Table.Header>
                          <Table.Row>
                            {csvPreview.columns.map((c) => (
                              <Table.HeaderCell key={c}>{c}</Table.HeaderCell>
                            ))}
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {csvPreview.sampleRows.map((row, idx) => (
                            <Table.Row key={idx}>
                              {csvPreview.columns.map((c) => (
                                <Table.Cell key={c}>{row[c] ?? '—'}</Table.Cell>
                              ))}
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  </>
                )}
              </Segment>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeCreateModal}>Cancelar</Button>
          <Button primary onClick={() => void handleCreateCampaign()} loading={creating}>
            Criar (rascunho)
          </Button>
        </Modal.Actions>
      </Modal>

      <ClientPickerModal
        open={clientPickerOpen}
        onClose={() => setClientPickerOpen(false)}
        onConfirm={(clients) => {
          setPrefillClients(clients);
          setRecipientSource('fromClients');
          setCreateRecipientsTab('phones');
          showSuccess(
            'Clientes adicionados. Revise variáveis padrão se houver payloads de botão (não vêm do cadastro).'
          );
        }}
      />

      <Modal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setRecipients(null);
        }}
        size="large"
      >
        <Modal.Header>Campanha</Modal.Header>
        <Modal.Content scrolling>
          {detailLoading && <Loader active />}
          {!detailLoading && detail && (
            <>
              <p>
                <strong>{detail.name}</strong>
              </p>
              {detail.description && <p>{detail.description}</p>}
              <p>
                Template: <code>{detail.templateId}</code>
              </p>
              <Label color={statusColor(detail.status)}>{detail.status}</Label>
              <Divider />
              <Table compact celled size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>Total</Table.Cell>
                    <Table.Cell>{detail.totalRecipients}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Pendentes</Table.Cell>
                    <Table.Cell>{detail.pendingCount}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Enviados</Table.Cell>
                    <Table.Cell>{detail.sentCount}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Falhas</Table.Cell>
                    <Table.Cell>{detail.failedCount}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
              {detail.defaultFields && Object.keys(detail.defaultFields).length > 0 && (
                <>
                  <Header as="h5">Variáveis padrão</Header>
                  <pre style={{ fontSize: '12px' }}>{JSON.stringify(detail.defaultFields, null, 2)}</pre>
                </>
              )}
              <Divider />
              <Header as="h5">Métricas</Header>
              {metricsLoading && <Loader active inline size="small" content="Calculando métricas…" />}
              {!metricsLoading && metrics && (
                <Segment>
                  <Grid columns={4} stackable>
                    <Grid.Column>
                      <strong>Total</strong>
                      <div>{metrics.totalRecipients}</div>
                    </Grid.Column>
                    <Grid.Column>
                      <strong>Enviados</strong>
                      <div>{metrics.sentCount}</div>
                    </Grid.Column>
                    <Grid.Column>
                      <strong>Falhas</strong>
                      <div>{metrics.failedCount}</div>
                    </Grid.Column>
                    <Grid.Column>
                      <strong>Taxa de sucesso</strong>
                      <div>
                        {metrics.totalRecipients > 0
                          ? (100 - metrics.failureRatePercent).toFixed(1)
                          : '0.0'}
                        %
                      </div>
                    </Grid.Column>
                  </Grid>
                  {metrics.errorBreakdown && metrics.errorBreakdown.length > 0 && (
                    <>
                      <Divider />
                      <strong>Top motivos de falha</strong>
                      <List bulleted>
                        {metrics.errorBreakdown.slice(0, 5).map((e, i) => (
                          <List.Item key={i}>
                            <code>{e.errorPattern || '—'}</code>: {e.count}
                          </List.Item>
                        ))}
                      </List>
                    </>
                  )}
                </Segment>
              )}
              <Divider />
              <Header as="h5">Destinatários</Header>
              <Segment>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
                  <Form.Field style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.85em' }}>Status</label>
                    <select
                      value={recipientsFilter.status ?? ''}
                      onChange={(e) =>
                        setRecipientsFilter((f) => ({ ...f, status: e.target.value || undefined }))
                      }
                      style={{ padding: '6px 8px' }}
                    >
                      <option value="">Todos</option>
                      <option value="Pending">Pending</option>
                      <option value="Sent">Sent</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Read">Read</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </Form.Field>
                  <Form.Field style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.85em' }}>Telefone / busca</label>
                    <input
                      type="text"
                      value={recipientsFilter.search ?? ''}
                      onChange={(e) =>
                        setRecipientsFilter((f) => ({ ...f, search: e.target.value || undefined }))
                      }
                      placeholder="ex.: 41984"
                      style={{ padding: '6px 8px' }}
                    />
                  </Form.Field>
                  <Form.Field style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.85em' }}>Erro contém</label>
                    <input
                      type="text"
                      value={recipientsFilter.errorContains ?? ''}
                      onChange={(e) =>
                        setRecipientsFilter((f) => ({ ...f, errorContains: e.target.value || undefined }))
                      }
                      placeholder="REJECTED"
                      style={{ padding: '6px 8px', width: '140px' }}
                    />
                  </Form.Field>
                  <Button size="small" primary onClick={() => loadRecipients(detail.id, 1)}>
                    Filtrar
                  </Button>
                  <Button
                    size="small"
                    basic
                    onClick={() => {
                      setRecipientsFilter({});
                      loadRecipients(detail.id, 1, {});
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    size="small"
                    color="green"
                    icon
                    labelPosition="left"
                    loading={exportingRecipients}
                    onClick={() => void handleExportRecipients()}
                  >
                    <Icon name="file excel" />
                    Exportar
                  </Button>
                  {selectedRecipientIds.size > 0 && (
                    <Button
                      size="small"
                      color="orange"
                      icon
                      labelPosition="left"
                      onClick={() => openRecreateModal('SelectedIds')}
                    >
                      <Icon name="redo" />
                      Refazer ({selectedRecipientIds.size})
                    </Button>
                  )}
                </div>
              </Segment>
              <p style={{ fontSize: '0.88em', color: '#555', marginBottom: '10px' }}>
                Na última coluna: em caso de <strong>falha</strong> aparece a mensagem de erro; em caso de{' '}
                <strong>sucesso</strong> aparece o JSON das variáveis (<code>fields</code>) que foram enviadas à Zenvia
                para aquele número — <em>não</em> é erro.
              </p>
              {recipientsLoading && <Loader active inline size="small" content="Carregando destinatários…" />}
              {!recipientsLoading && recipients && recipients.total > 0 && (
                <>
                  <div style={{ marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.9em' }}>
                      Página {recipients.page} de {Math.max(1, Math.ceil(recipients.total / recipientPageSize))} (
                      {recipients.total} números)
                    </span>
                    <Button
                      size="mini"
                      disabled={recipients.page <= 1}
                      onClick={() => loadRecipients(detail.id, recipients.page - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="mini"
                      disabled={recipients.page >= Math.ceil(recipients.total / recipientPageSize)}
                      onClick={() => loadRecipients(detail.id, recipients.page + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <Table celled striped compact size="small">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell collapsing>
                            <input
                              type="checkbox"
                              checked={
                                recipients.items.length > 0 &&
                                recipients.items.every((r) => selectedRecipientIds.has(r.id))
                              }
                              onChange={toggleSelectAllInPage}
                              title="Selecionar todos da página"
                            />
                          </Table.HeaderCell>
                          <Table.HeaderCell>Telefone</Table.HeaderCell>
                          <Table.HeaderCell>Status</Table.HeaderCell>
                          <Table.HeaderCell>Id Zenvia</Table.HeaderCell>
                          <Table.HeaderCell>Processado</Table.HeaderCell>
                          <Table.HeaderCell>Variáveis (JSON) ou erro</Table.HeaderCell>
                          <Table.HeaderCell>Ações</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {recipients.items.map((row) => {
                          const errorTooltip = row.errorMessage || row.errorCode
                            ? `${row.errorCode ? '[' + row.errorCode + '] ' : ''}${row.errorMessage ?? ''}${
                                row.errorRawJson ? '\n\nPayload:\n' + row.errorRawJson : ''
                              }`
                            : null;
                          return (
                            <Table.Row key={row.id}>
                              <Table.Cell collapsing>
                                <input
                                  type="checkbox"
                                  checked={selectedRecipientIds.has(row.id)}
                                  onChange={() => toggleSelectRecipient(row.id)}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <code style={{ fontSize: '0.85em' }}>{row.phone}</code>
                              </Table.Cell>
                              <Table.Cell>
                                <Label size="small" color={recipientStatusColor(row.status)}>
                                  {row.status}
                                </Label>
                              </Table.Cell>
                              <Table.Cell>
                                {row.externalMessageId ? (
                                  <code style={{ fontSize: '0.75em', wordBreak: 'break-all' }}>{row.externalMessageId}</code>
                                ) : (
                                  '—'
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                {row.processedAt
                                  ? new Date(row.processedAt).toLocaleString('pt-BR', { timeZone: 'UTC' }) + ' UTC'
                                  : '—'}
                              </Table.Cell>
                              <Table.Cell style={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {errorTooltip ? (
                                  <Popup
                                    on="click"
                                    position="top left"
                                    trigger={
                                      <span style={{ color: '#9f3a38', cursor: 'pointer', textDecoration: 'underline dotted' }}>
                                        {row.errorCode ? <strong>[{row.errorCode}] </strong> : null}
                                        {row.errorMessage || 'Ver detalhes'}
                                      </span>
                                    }
                                    wide="very"
                                    content={
                                      <pre style={{ maxWidth: 480, maxHeight: 320, overflow: 'auto', fontSize: '0.75em', whiteSpace: 'pre-wrap' }}>
                                        {errorTooltip}
                                      </pre>
                                    }
                                  />
                                ) : row.fields && Object.keys(row.fields).length > 0 ? (
                                  <code style={{ fontSize: '0.75em' }}>{JSON.stringify(row.fields)}</code>
                                ) : (
                                  '—'
                                )}
                              </Table.Cell>
                              <Table.Cell textAlign="center">
                                {row.status === 'Failed' && (
                                  <Button
                                    icon="redo"
                                    size="mini"
                                    color="orange"
                                    title="Reenviar este destinatário"
                                    loading={retryingRecipientId === row.id}
                                    disabled={retryingRecipientId !== null}
                                    onClick={() => handleRetryOne(detail.id, row.id)}
                                  />
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                </>
              )}
              {!recipientsLoading && recipients && recipients.total === 0 && (
                <p style={{ color: '#888' }}>Nenhum destinatário nesta campanha.</p>
              )}
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => detail && refreshDetail(detail.id)}>
                  Atualizar
                </Button>
                {detail.failedCount > 0 && (
                  <Button
                    size="small"
                    color="orange"
                    loading={retryingAll}
                    onClick={() => handleRetryFailed(detail.id)}
                  >
                    Reenviar {detail.failedCount} falha(s)
                  </Button>
                )}
                <Button
                  size="small"
                  color="violet"
                  icon
                  labelPosition="left"
                  onClick={() => openRecreateModal('FailedOnly')}
                >
                  <Icon name="redo" />
                  Refazer disparo…
                </Button>
              </div>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDetailModal(false)}>Fechar</Button>
        </Modal.Actions>
      </Modal>

      <Modal open={showRecreateModal} onClose={() => setShowRecreateModal(false)} size="small">
        <Modal.Header>Refazer disparo</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Nome da nova campanha</label>
              <input
                type="text"
                value={recreateName}
                onChange={(e) => setRecreateName(e.target.value)}
                placeholder="Deixe em branco para usar o padrao"
              />
            </Form.Field>
            <Form.Field>
              <label>Origem dos destinatarios</label>
              <select
                value={recreateSource}
                onChange={(e) => setRecreateSource(e.target.value as ZenviaRecreateSource)}
                style={{ padding: '6px 8px', width: '100%' }}
              >
                <option value="FailedOnly">Apenas falhas</option>
                <option value="All">Todos</option>
                <option value="SelectedIds">Selecionados ({selectedRecipientIds.size})</option>
              </select>
            </Form.Field>
            <Form.Field>
              <label>Pular erros que correspondam a (um por linha, opcional)</label>
              <TextArea
                value={recreateSkipPatterns}
                onChange={(_, { value }) => setRecreateSkipPatterns(String(value ?? ''))}
                placeholder={'REJECTED\nNOT_DELIVERED\ninvalid number'}
                rows={4}
              />
              <small style={{ color: '#888' }}>
                Comparacao case-insensitive contra <code>errorCode</code> e <code>errorMessage</code>.
              </small>
            </Form.Field>
            <Form.Field>
              <label>
                <input
                  type="checkbox"
                  checked={recreateAutoStart}
                  onChange={(e) => setRecreateAutoStart(e.target.checked)}
                />{' '}
                Iniciar automaticamente apos criar
              </label>
            </Form.Field>
            <Message info size="tiny">
              Os campos longos (<code>NOME</code>, <code>razao_social</code>, <code>EMPRESA</code>) serao truncados
              automaticamente conforme os limites configurados.
            </Message>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowRecreateModal(false)} disabled={recreating}>
            Cancelar
          </Button>
          <Button primary loading={recreating} onClick={() => void handleRecreate()}>
            Criar nova campanha
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default WhatsAppBatchesPage;
